import { Button } from "@/components/ui/button";
import { CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Upload } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";


export interface ResumeUploadProps {
  onUpload: () => void;
}

const ResumeUpload: React.FC<ResumeUploadProps> = ({ onUpload }) => {


  const { user, token } = useAuth();
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || '/api';

  const getAxiosConfig = () => {
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      withCredentials: true
    };
  };

  const getFileUploadConfig = () => {
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true
    };
  };

  const handleResumeUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setResumeFile(e.target.files[0]);
    }
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      setResumeFile(droppedFile);
    }
  };


  const handleUploadResume = async () => {
    if (!resumeFile) {
      toast.error('Please select a resume file');
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('document', resumeFile);

      const response = await axios.post(
        `${API_URL}/me/from-document`,
        formData,
        getFileUploadConfig()
      );

      if (response.data) {
        toast.success('Resume reviewed!');
        onUpload();
      } else {
        toast.error('Failed to upload resume');
      }
    } catch (error) {
      console.error('Error uploading resume:', error);
      toast.error('Failed to upload resume');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <CardHeader>
        <CardTitle>Upload Resume</CardTitle>
        <CardDescription>
          Upload your resume so we can let our Coach know about you
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
            isDragActive
              ? "border-interview-blue bg-interview-blue/5"
              : "border-gray-300"
          }`}
          onDragEnter={(e) => {
            e.preventDefault();
            setIsDragActive(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (!isDragActive) {
              setIsDragActive(true);
            }
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            if (e.currentTarget === e.target) {
              setIsDragActive(false);
            }
          }}
          onDrop={handleFileDrop}
        >
          <div className="mx-auto flex flex-col items-center justify-center">
            <Upload className="h-10 w-10 text-gray-400 mb-4" />

            <h3 className="text-lg font-medium mb-1">
              {resumeFile ? resumeFile.name : 'Drag and drop your resume'}
            </h3>

            <p className="text-sm text-gray-500 mb-4">
              PDF, DOCX, or TXT (max 5MB)
            </p>

            <Label htmlFor="resume-upload" className="cursor-pointer">
              <div className="bg-interview-blue text-white py-2 px-4 rounded-md hover:bg-interview-blue/90">
                Browse Files
              </div>
              <Input
                id="resume-upload"
                type="file"
                className="hidden"
                accept=".pdf,.docx,.doc,.txt"
                onChange={handleResumeUpload}
              />
            </Label>
          </div>
        </div>

        <div className="mt-6 flex justify-center space-x-3">
          <Button onClick={handleUploadResume} disabled={isLoading || !resumeFile}>
            {isLoading ? 'Uploading...' : 'Upload Resume'}
          </Button>
        </div>
      </CardContent>
    </>
  );
};

export default ResumeUpload;
